import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useInstance } from "easy-redux-http-call";
import { useAIEventHandler } from "easy-redux-http-call/dist/index";
import { useNavigate } from "react-router-dom";
import { AI_getUserData } from "APIs/user";
import { AI_missedPunchToday, AI_missedPunchAddLeave } from "APIs/missedPunchAlert";

const ALERT_HOUR_IST = 12;
const RETRY_WINDOW_MINUTES = 5;
const RETRY_EVERY_MS = 30 * 1000;
const DEFAULT_REASON = "Added by HR – no punch-in";

// Current time in IST as { dateStr: "YYYY-MM-DD", seconds: seconds since IST midnight }
const getIstNow = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
    .formatToParts(new Date())
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return {
    dateStr: `${parts.year}-${parts.month}-${parts.day}`,
    seconds: Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second),
  };
};

const dismissKey = (employeeId, dateStr) => `missedPunchAlertDismissed:${employeeId}:${dateStr}`;

const readDismissed = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch (e) {
    return false;
  }
};

const MissedPunchAlertModal = () => {
  const navigate = useNavigate();
  const userData = useInstance(AI_getUserData);
  const todayState = useInstance(AI_missedPunchToday);
  const employeeId = userData?.data?.user?.employee_id;

  const [open, setOpen] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const [activeEmployee, setActiveEmployee] = useState(null);
  const [leaveType, setLeaveType] = useState("fullday");
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [description, setDescription] = useState("");
  const [reasonError, setReasonError] = useState("");
  const eligibleRef = useRef(true);
  const timerRef = useRef(null);

  const fetchAlert = () => AI_missedPunchToday.call({});

  // Fetch at 12:00 PM IST (or right away if already past it), retrying briefly while the cron finishes.
  useEffect(() => {
    if (!employeeId) return undefined;

    const schedule = () => {
      const { seconds } = getIstNow();
      const alertAt = ALERT_HOUR_IST * 3600;
      const retryUntil = alertAt + RETRY_WINDOW_MINUTES * 60;

      if (seconds < alertAt) {
        // +15s so the 12:00:00 cron has time to write its record
        timerRef.current = setTimeout(schedule, (alertAt - seconds + 15) * 1000);
        return;
      }
      if (!eligibleRef.current) return;
      fetchAlert();
      if (seconds < retryUntil) {
        timerRef.current = setTimeout(schedule, RETRY_EVERY_MS);
      }
    };
    schedule();
    return () => clearTimeout(timerRef.current);
  }, [employeeId]);

  useEffect(() => {
    const data = todayState?.data;
    if (!data) return;
    eligibleRef.current = data.eligible !== false;
    if (data.alert) {
      setAlertData(data.alert);
      if (!readDismissed(dismissKey(employeeId, data.alert.date))) setOpen(true);
    } else {
      setAlertData(null);
      setOpen(false);
    }
  }, [todayState?.data]);

  useAIEventHandler(AI_missedPunchAddLeave, {
    onSuccess: () => {
      setActiveEmployee(null);
      fetchAlert();
    },
  });

  const handleClose = () => {
    if (alertData) {
      try {
        localStorage.setItem(dismissKey(employeeId, alertData.date), "1");
      } catch (e) {
        // storage unavailable: modal will simply reappear on next load
      }
    }
    setOpen(false);
  };

  const startAddLeave = (emp) => {
    setActiveEmployee(emp);
    setLeaveType("fullday");
    setReason(DEFAULT_REASON);
    setDescription("");
    setReasonError("");
  };

  const submitLeave = () => {
    if (!reason.trim()) {
      setReasonError("Reason is required");
      return;
    }
    AI_missedPunchAddLeave.call({
      params: {
        employee_id: activeEmployee.id,
        leave_type: leaveType,
        reason: reason.trim(),
        description,
      },
    });
  };

  if (!alertData) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h4">
          {alertData.status === "failed" ? "Punch-in check failed" : "No punch-in recorded"}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {alertData.status === "failed" ? (
          <Alert severity="error">
            Today's 12:00 PM punch-in check could not be completed, so absentees could not be identified.
            {alertData.message ? ` (${alertData.message})` : ""} Please check attendance manually.
          </Alert>
        ) : (
          <>
            <Typography sx={{ mb: 2 }}>
              These employees have no approved leave and have not punched in as of 12:00 PM today.
            </Typography>
            {alertData.employees.map((emp) => (
              <div key={emp.id} style={{ borderBottom: "1px solid #eee", padding: "8px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography style={{ fontWeight: 600 }}>{emp.name}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => (activeEmployee?.id === emp.id ? setActiveEmployee(null) : startAddLeave(emp))}
                  >
                    {activeEmployee?.id === emp.id ? "Cancel" : "Add leave"}
                  </Button>
                </div>
                {activeEmployee?.id === emp.id && (
                  <div style={{ marginTop: 12 }}>
                    <RadioGroup row value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
                      <FormControlLabel value="fullday" control={<Radio />} label="Full day" />
                      <FormControlLabel value="firstHalf" control={<Radio />} label="First half" />
                      <FormControlLabel value="secondHalf" control={<Radio />} label="Second half" />
                    </RadioGroup>
                    <TextField
                      fullWidth
                      size="small"
                      label="Reason"
                      value={reason}
                      error={Boolean(reasonError)}
                      helperText={reasonError}
                      onChange={(e) => {
                        setReason(e.target.value);
                        setReasonError("");
                      }}
                      sx={{ mt: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Description (optional)"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      minRows={2}
                      sx={{ mt: 1.5 }}
                    />
                    <Button variant="contained" onClick={submitLeave} sx={{ mt: 1.5 }}>
                      Add approved leave for today
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Button
            size="small"
            onClick={() => {
              handleClose();
              navigate("/missed-punch-history");
            }}
          >
            View past alerts
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MissedPunchAlertModal;
